"use client";

import { Button, InlineNotification } from "@carbon/react";
import type { ComponentProps, ReactNode, Ref } from "react";

type FormLayout =
  | "stack"
  | "compact"
  | "combo"
  | "dialog"
  | "inline"
  | "section"
  | "plain";

type FormShellProps = Omit<ComponentProps<"form">, "className" | "children"> & {
  layout?: FormLayout;
  width?: "sm" | "md" | "lg" | "full";
  formRef?: Ref<HTMLFormElement>;
  className?: string;
  error?: string | null;
  errorTitle?: string;
  saved?: boolean;
  savedMessage?: string;
  savedTitle?: string;
  info?: string | null;
  infoTitle?: string;
  submitLabel?: string;
  pendingLabel?: string;
  pending?: boolean;
  submitDisabled?: boolean;
  submitKind?: ComponentProps<typeof Button>["kind"];
  submitSize?: ComponentProps<typeof Button>["size"];
  hideSubmit?: boolean;
  cancelLabel?: string;
  onCancel?: () => void;
  children?: ReactNode;
};

const LAYOUT_CLASS: Record<FormLayout, string> = {
  stack: "settings-form",
  compact: "form-compact form-compact-name",
  combo: "form-compact form-compact-name form-compact-combo",
  dialog: "form-dialog",
  inline: "form-shell-inline",
  section: "settings-form-section",
  plain: "",
};

const WIDTH_CLASS = {
  sm: "form-shell-sm",
  md: "",
  lg: "form-shell-lg",
  full: "w-full",
};

function cx(...parts: Array<string | false | undefined | null>) {
  return parts.filter(Boolean).join(" ");
}

function FormNotice({
  kind,
  title,
  subtitle,
  compact,
}: {
  kind: "error" | "success" | "info";
  title: string;
  subtitle: string;
  compact: boolean;
}) {
  return (
    <InlineNotification
      className={compact ? "form-compact-alert" : undefined}
      kind={kind}
      title={title}
      subtitle={subtitle}
      hideCloseButton
      lowContrast
    />
  );
}

export function FormShell({
  layout = "stack",
  width = "md",
  formRef,
  className,
  error,
  errorTitle,
  saved = false,
  savedMessage,
  savedTitle,
  info,
  infoTitle,
  submitLabel,
  pendingLabel,
  pending = false,
  submitDisabled = false,
  submitKind,
  submitSize = "md",
  hideSubmit = false,
  cancelLabel,
  onCancel,
  children,
  ...formProps
}: FormShellProps) {
  const compact = layout === "compact" || layout === "combo";
  const noticeTitle = errorTitle ?? savedTitle ?? infoTitle ?? "";
  const kind =
    submitKind ??
    (layout === "dialog" || layout === "stack" || layout === "section"
      ? "primary"
      : "tertiary");

  const submitButton =
    hideSubmit || !submitLabel ? null : (
      <Button
        type="submit"
        kind={kind}
        size={submitSize}
        disabled={pending || submitDisabled}
      >
        {pending ? (pendingLabel ?? submitLabel) : submitLabel}
      </Button>
    );

  const cancelButton =
    cancelLabel && onCancel ? (
      <Button
        type="button"
        kind="secondary"
        size={submitSize}
        disabled={pending}
        onClick={onCancel}
      >
        {cancelLabel}
      </Button>
    ) : null;

  const wrapActions =
    layout === "dialog" || layout === "stack" || layout === "section";
  const actions =
    submitButton || cancelButton ? (
      wrapActions ? (
        <div
          className={
            layout === "dialog" ? "form-dialog-actions" : "settings-form-actions"
          }
        >
          {cancelButton}
          {submitButton}
        </div>
      ) : (
        <>
          {cancelButton}
          {submitButton}
        </>
      )
    ) : null;

  return (
    <form
      {...formProps}
      ref={formRef}
      className={cx(LAYOUT_CLASS[layout], WIDTH_CLASS[width], className)}
    >
      {error ? (
        <FormNotice
          kind="error"
          title={noticeTitle}
          subtitle={error}
          compact={compact}
        />
      ) : null}
      {saved && savedMessage && !error ? (
        <FormNotice
          kind="success"
          title={savedTitle ?? noticeTitle}
          subtitle={savedMessage}
          compact={compact}
        />
      ) : null}
      {info && !error ? (
        <FormNotice
          kind="info"
          title={infoTitle ?? noticeTitle}
          subtitle={info}
          compact={compact}
        />
      ) : null}
      {children}
      {actions}
    </form>
  );
}

type FormSectionProps = {
  title?: string;
  help?: ReactNode;
  className?: string;
  children?: ReactNode;
};

export function FormSection({
  title,
  help,
  className,
  children,
}: FormSectionProps) {
  return (
    <section className={cx("settings-form-section", className)}>
      {title ? <h2 className="settings-form-title">{title}</h2> : null}
      {help ? <p className="settings-form-help">{help}</p> : null}
      {children}
    </section>
  );
}

export function FormFields({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("settings-form-fields", className)}>{children}</div>
  );
}
